import { defineNuxtPlugin } from '#app'

type LocaleMessages = Record<string, any>

const deepMerge = (target: LocaleMessages, source: LocaleMessages): LocaleMessages => {
  const output = Array.isArray(target) ? [...target] : { ...target }
  for (const key of Object.keys(source)) {
    const sourceValue = source[key]
    const targetValue = output[key]
    if (
      sourceValue &&
      typeof sourceValue === 'object' &&
      !Array.isArray(sourceValue)
    ) {
      output[key] = deepMerge(
        targetValue && typeof targetValue === 'object' ? targetValue : {},
        sourceValue
      )
    } else {
      output[key] = sourceValue
    }
  }
  return output
}

const mapMessages = {
  common: {
    hideMap: 'Hide Map'
  },
  map: {
    storeLocation: 'Store Location',
    openInMaps: 'Open in Maps',
    loading: 'Loading map...',
    enableLocation: 'Enable Location',
    closeMap: 'Close Map',
    switchToColor: 'Switch to Color Map',
    switchToLight: 'Switch to B&W Map',
    byCar: 'By Car',
    onFoot: 'On Foot',
    byBike: 'By Bike',
    recenter: 'Recenter',
    addressLabel: 'Address',
    contactLabel: 'Contact',
    openingHours: 'Opening Hours',
    interactiveMap: 'Interactive Map',
    mapboxRequired: 'Mapbox integration required',
    close: 'Close',
    howToGetThere: 'How to Get There',
    closed: 'Closed',
    durationHoursMinutes: '{hours}h {minutes}m',
    durationMinutes: '{minutes}m',
    days: {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday'
    },
    directions: {
      metroTitle: 'Metro',
      metroDescription: 'Luxembourg Station (RER B)',
      walkingTitle: 'Walking',
      walkingDescription: '5 minutes from Luxembourg Gardens',
      parkingTitle: 'Parking',
      parkingDescription: 'Limited street parking available'
    },
    defaults: {
      weekday: 'Monday - Friday',
      weekdayHours: '10:30 - 19:30',
      saturday: 'Saturday',
      saturdayHours: '11:00 - 18:00'
    },
    errors: {
      config: 'Map configuration error. Please contact support.',
      webgl: 'WebGL is not supported in this environment. Please try a different browser.',
      webglLimited: 'WebGL is not supported. Map functionality will be limited.',
      webglExtended: 'WebGL is not supported in this environment. Please try a different browser or enable hardware acceleration.',
      generic: 'Unable to load map. Please try again later.',
      location: 'Unable to get your location. Please check your browser settings.',
      token: 'Mapbox configuration error. Please check your environment variables.'
    }
  }
}

const mapMessagesFr: LocaleMessages = {
  common: {
    hideMap: 'Masquer la carte'
  },
  map: {
    storeLocation: 'Localisation de la boutique',
    openInMaps: 'Ouvrir dans Maps',
    loading: 'Chargement de la carte…',
    enableLocation: 'Activer la localisation',
    closeMap: 'Fermer la carte',
    switchToColor: 'Passer à la carte couleur',
    switchToLight: 'Passer à la carte noir & blanc',
    byCar: 'En voiture',
    onFoot: 'À pied',
    byBike: 'À vélo',
    recenter: 'Recentrer',
    addressLabel: 'Adresse',
    contactLabel: 'Contact',
    openingHours: "Horaires d'ouverture",
    interactiveMap: 'Carte interactive',
    mapboxRequired: 'Intégration Mapbox requise',
    close: 'Fermer',
    howToGetThere: "Comment s'y rendre",
    closed: 'Fermé',
    durationHoursMinutes: '{hours} h {minutes} min',
    durationMinutes: '{minutes} min',
    days: {
      monday: 'Lundi',
      tuesday: 'Mardi',
      wednesday: 'Mercredi',
      thursday: 'Jeudi',
      friday: 'Vendredi',
      saturday: 'Samedi',
      sunday: 'Dimanche'
    },
    directions: {
      metroTitle: 'Métro',
      metroDescription: 'Station Luxembourg (RER B)',
      walkingTitle: 'À pied',
      walkingDescription: 'À 5 minutes du Jardin du Luxembourg',
      parkingTitle: 'Parking',
      parkingDescription: 'Stationnement dans la rue en quantité limitée'
    },
    defaults: {
      weekday: 'Lundi - Vendredi',
      weekdayHours: '10h30 - 19h30',
      saturday: 'Samedi',
      saturdayHours: '11h00 - 18h00'
    },
    errors: {
      config: 'Erreur de configuration de la carte. Merci de contacter le support.',
      webgl: "WebGL n'est pas pris en charge dans cet environnement. Veuillez essayer un autre navigateur.",
      webglLimited: "WebGL n'est pas pris en charge. Les fonctionnalités de la carte seront limitées.",
      webglExtended: "WebGL n'est pas pris en charge dans cet environnement. Essayez un autre navigateur ou activez l'accélération matérielle.",
      generic: 'Impossible de charger la carte. Veuillez réessayer plus tard.',
      location: "Impossible d'obtenir votre position. Vérifiez les réglages de votre navigateur.",
      token: "Erreur de configuration Mapbox. Vérifiez vos variables d'environnement."
    }
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const i18n = nuxtApp.$i18n
  if (!i18n) {
    return
  }

  const mergeLocale = (locale: string, messages: LocaleMessages) => {
    const existing = i18n.getLocaleMessage(locale) || {}
    const merged = deepMerge(existing, messages)
    i18n.setLocaleMessage(locale, merged)
  }

  mergeLocale('en', mapMessages)
  mergeLocale('fr', mapMessagesFr)
})
